import { ChoiceListProps } from "@shopify/polaris"
import dayjs from "dayjs"

import { IFilterOptionBase } from "@/api/orders/type"
import { SortButtonDirection } from "@/component"
import { TableItem } from "@/models/IOrders"
import type { FilterOperatorState } from "@/pages/Orders/utils/filterOperator"

export interface FilterParam {
  orderedAt?: OrderedAt
  orderedAtMin?: string
  orderedAtMax?: string
  /** 单号状态 */
  status?: Status
  /** 国家（整国选中时） */
  countries?: string[]
  /** 目的地地区码（部分子节点选中时，如 US-VA） */
  destinationRegionCode?: string[]
  /** 运输商 */
  couriers?: string[]
  /** 运输时长 */
  transitTime?: [number, number]
  /** 查询字符 */
  filterValue?: string
  /** 排序 */
  orderBy?: OrderBy
  /** 页码 */
  page?: number
}

type OrderedAt = "Today" | "Last 7 days" | "Last 30 days" | "Last 60 days" | "Last 90 days" | "Last 120 days"


type Status =
  "Pending"
  | "In transit"
  | "Out for delivery"
  | "Delivered"
  | "Expired"
  | "Failed attempt"
  | "Exception"
  | "Info received"


type OrderField = string

export enum OrderDirection {
  DESC = -1,
  ASC = 1,
}

export type OrderBy = { [orderField: OrderField]: OrderDirection }


export enum QueryParam {
  orderedAt = "orderedAt",
  orderedAtMin = "orderedAtMin",
  orderedAtMax = "orderedAtMax",
  fulfilledAt = "fulfilledAt",
  fulfilledAtMin = "fulfilledAtMin",
  fulfilledAtMax = "fulfilledAtMax",

  pickupDate = "pickupDate",
  pickupDateMin = "pickupDateMin",
  pickupDateMax = "pickupDateMax",

  deliveryDate = "deliveryDate",
  deliveryDateMin = "deliveryDateMin",
  deliveryDateMax = "deliveryDateMax",

  lastCheckpointTime = "lastCheckpointTime",
  lastCheckpointTimeMin = "lastCheckpointTimeMin",
  lastCheckpointTimeMax = "lastCheckpointTimeMax",

  status = "status",
  paypalSyncStatus = "paypalSyncStatus",
  countries = "countries",
  destinationRegionCode = "destinationRegionCode",
  couriers = "couriers",
  lastMile = "lastMileCarrierIds",
  locations = "locations",
  orderTags = "orderTags",
  transitTime = "transitTime",
  residenceTime = "residenceTime",
  processingTime = "processingTime",
  pickupTime = "pickupTime",
  orderToDeliveryTime = "orderToDeliveryTime",
  Query = "query",
  orderBy = "orderBy",
  page = "page",
  perPage = "perPage",
  shippingProtectionStatus="shippingProtectionStatus",
  /** 各筛选项的匹配方式，见 utils/filterOperator */
  filterOperator = "filter_operator",
}

type Choice = ChoiceListProps["choices"][number]

type ChoicesSelected = string[]

export type QueryParamsType = {
  [k in QueryParam]?: any
}

export enum OrderReSyncStatus {
  Unknown = 0,
  Synchronizing = 10,
  SyncComplete = 20
}

export interface ITabNav {
  // title: {};
  id: string
  /** 文本内容, 会在无需翻译的项中存在, 比如自定义视图 */
  content?: string
  total?: number
  filters_append?: boolean
  filters?: QueryParamsType
  tran_key?: string // 2022-04-20 新加字段
  isCustomized?: boolean
}

export interface IDateOption {
  selected?: string
  starting: dayjs.Dayjs
  ending: dayjs.Dayjs
}

export enum DateOption {
  orderedAt = "orderedAt",
  fulfilledAt = "fulfilledAt",
  pickupDate = "pickupDate",
  deliveryDate = "deliveryDate",
  lastCheckpointTime = "lastCheckpointTime",
}

export interface IOrdersState {
  /** 是否为首次加载*/
  isFirstLoad: boolean,

  /** 当前一级导航参数 */
  selectedTabIndex: number
  /** 选中的 tab 项 ID */
  selectedTabId: string

  /** 是否选中自定义 tab */
  isSelectedCustomNav: boolean

  /** 导航项数量 */
  tabsShipmentCount: Record<string, number>

  navTitle: string
  navSaved: boolean

  /** 同步时间选择列表 */
  syncTimeList: any[]

  orderMessage: {
    tableData: TableItem[]
    numberCount: number
    /** 本次分页实际生效的每页条数 */
    perPage?: number
  }

  /** 表格页码 */
  tablePageNum: number
  prevPage?: number
  nextPage?: number

  pageConfigLoading: boolean
  ordersLoading: boolean

  queryValue: string

  dateSelected?: string
  dateStartingSelected: dayjs.Dayjs
  dateEndingSelected: dayjs.Dayjs

  // todo dateSelected → orderedDateOption
  // orderedDateOption: IDateOption
  fulfilledDateOption: IDateOption
  pickupDateOption: IDateOption
  deliveryDateOption: IDateOption
  lastCheckpointTimeOption: IDateOption

  statusChoices: CustomChoice[]
  statusSelected: ChoicesSelected
  courierChoices: CustomChoice[]
  lastMileChoices: CustomChoice[]
  courierSelected: ChoicesSelected
  lastMileSelected: ChoicesSelected
  locationChoices: CustomChoice[]
  locationSelected: ChoicesSelected
  destinationChoices: CustomChoice[]
  /** 目的地筛选：整国用 countries，部分子节点用 destinationRegionCode */
  destinationFilter: DestinationFilterValue
  paypalSyncStatusChoices: CustomChoice[]
  /**
 * 运输险选项
 */
  shippingProtectionStatusChoices:CustomChoice[]
  /**
   * 运输险状态
   */
  shippingProtectionStatus: ChoicesSelected
  paypalSyncStatusSelected: ChoicesSelected
  orderTagChoices: CustomChoice[]
  /** Order tag 筛选项里的搜索关键词 */
  orderTagsKeyword: string
  /** 搜索结果. 因为需要让 Collapsible 组件重新计算高度, 所以抽离到父组件上进行搜索了 */
  orderTagChoicesShow?: CustomChoice[]
  orderTagSelected: ChoicesSelected

  /** Courier 筛选项里的搜索关键词 */
  courierKeyword: string
  /** Courier 搜索结果 */
  courierChoicesShow?: CustomChoice[]

  /** Location 筛选项里的搜索关键词 */
  locationKeyword: string
  /** Location 搜索结果 */
  locationChoicesShow?: CustomChoice[]

  /** Destination 筛选项里的搜索关键词 */
  destinationKeyword: string
  /** Destination 搜索结果 */
  destinationChoicesShow?: CustomChoice[]

  /** Last Mile Carrier 筛选项里的搜索关键词 */
  lastMileKeyword: string
  /** Last Mile Carrier 搜索结果 */
  lastMileChoicesShow?: CustomChoice[]

  transitTimeValue?: [number, number]
  residenceTimeValue?: [number, number]
  processingTimeValue?: [number, number]
  pickupTimeValue?: [number, number]
  orderToDeliveryTimeValue?: [number, number]

  perPage?: number
  /** 每页条数可选档位，取 order-default 的 perPageOptions */
  perPageOptions: number[]

  /** 多选类筛选项的 is / is not */
  filterOperator: FilterOperatorState

  reSync: {
    /** 数据是否加载完成 */
    loaded: boolean
    count: number// 订单数量
    show: boolean// 是否显示弹窗
    status: OrderReSyncStatus// 同步状态
  }

  // 日期筛选项是否选中了 Custom 项；用于触发组件刷新，使界面正常显示出日期选择器
  isDateFilterCustomItemSelected: boolean

  /** 排序字段 */
  sortSelected: string
  /** 排序方向 */
  sortDirectionSelected: SortButtonDirection

  headingColumnPopoverActive: boolean

  exception_shipment_banner_closed: boolean // shipment_banner是否关闭
}

/** 目的地筛选值：整国用 countries，部分地区用 destinationRegionCode */
export interface DestinationFilterValue {
  countries: string[]
  destinationRegionCode: string[]
}

export type CustomChoice = Choice & Pick<IFilterOptionBase, "tran_key"> & {
  count: number
  children: {
    value: number;
    count: number;
  }[];
}
